import React, { useState } from "react";
import { Image as ImageIcon, Loader2, RefreshCw, Sparkles } from "lucide-react";
import { ConceptMemo } from "./ConceptMemo";
import { api } from "../lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function absoluteUrl(url) {
  if (!url) return null;
  return url.startsWith("http") ? url : `${BACKEND}${url}`;
}

export const OpportunityConceptMemo = ({ opportunity, onUpdate }) => {
  const [retrying, setRetrying] = useState(false);
  const [error, setError] = useState("");
  const memo = opportunity.concept_memo;
  const memoActive = ["queued", "processing"].includes(opportunity.memo_status);
  const imageActive = ["queued", "processing"].includes(opportunity.packshot_status);
  const failed = opportunity.memo_status === "failed" || opportunity.packshot_status === "failed";

  const retry = async () => {
    setRetrying(true);
    setError("");
    try {
      await api.retryOpportunityMemo(opportunity.id);
      const fresh = await api.opportunity(opportunity.id);
      onUpdate(fresh);
    } catch (requestError) {
      setError(requestError.response?.data?.detail || requestError.message || "Retry failed");
    } finally {
      setRetrying(false);
    }
  };

  if (!memo) {
    return (
      <section className="border border-bm-amber/40 bg-bm-surface p-6" data-testid="opportunity-memo-pending">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-start gap-3">
            {memoActive ? <Loader2 className="w-5 h-5 text-bm-amber animate-spin shrink-0" /> : <Sparkles className="w-5 h-5 text-bm-amber shrink-0" />}
            <div>
              <div className="label-mono text-bm-amber">PRODUCT CONCEPT MEMO</div>
              <h2 className="font-heading text-xl font-bold mt-1">{memoActive ? "Memo generation in progress" : "Memo not generated yet"}</h2>
              <p className="text-sm text-bm-muted mt-2">
                {opportunity.memo_status === "failed" ? opportunity.memo_error : "The Opportunity remains available while its editorial Memo and product packshot are created in the background."}
              </p>
            </div>
          </div>
          {!memoActive && (
            <button type="button" onClick={retry} disabled={retrying} data-testid="retry-opportunity-memo"
              className="inline-flex items-center justify-center gap-2 px-4 py-2 border border-bm-amber/60 text-bm-amber font-mono text-xs hover:bg-bm-amber hover:text-black transition-colors disabled:opacity-50">
              {retrying ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              {opportunity.memo_status === "failed" ? "RETRY MEMO" : "GENERATE MEMO"}
            </button>
          )}
        </div>
        {error && <div className="text-xs font-mono text-bm-danger mt-3" data-testid="opportunity-memo-retry-error">{error}</div>}
      </section>
    );
  }

  return (
    <section className="space-y-3" data-testid="opportunity-concept-memo">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-white/10 pb-3">
        <div>
          <div className="label-mono text-bm-amber">PRODUCT CONCEPT MEMO</div>
          <div className="flex items-center gap-2 text-xs font-mono text-bm-muted mt-1">
            <span>MEMO / {opportunity.memo_status?.toUpperCase()}</span>
            <span>·</span>
            <span data-testid="opportunity-packshot-status">PACKSHOT / {opportunity.packshot_status?.toUpperCase()}</span>
          </div>
        </div>
        {failed && (
          <button type="button" onClick={retry} disabled={retrying} data-testid="retry-opportunity-packshot"
            className="inline-flex items-center gap-2 px-3 py-2 border border-bm-danger/60 text-bm-danger font-mono text-xs hover:bg-bm-danger hover:text-white transition-colors disabled:opacity-50">
            {retrying ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            RETRY IMAGE
          </button>
        )}
      </div>
      {opportunity.packshot_status === "failed" && (
        <div className="border border-bm-danger/40 p-3 flex items-start gap-2 text-xs font-mono text-bm-muted" data-testid="opportunity-packshot-error">
          <ImageIcon className="w-4 h-4 text-bm-danger shrink-0" />
          <span>The Memo is preserved. Product image generation failed: {opportunity.packshot_error}</span>
        </div>
      )}
      {error && <div className="text-xs font-mono text-bm-danger" data-testid="opportunity-memo-retry-error">{error}</div>}
      <div className="border border-bm-amber/30 bg-bm-amber/5 px-3 py-2 text-[11px] font-mono text-bm-muted" data-testid="opportunity-memo-disclaimer">
        PRODUCT CREATIVE ARTIFACT · PROPOSED INGREDIENTS, CLAIMS AND RESULTS REQUIRE VALIDATION
      </div>
      <ConceptMemo memo={memo} packshotUrl={absoluteUrl(opportunity.packshot_url)} packshotLoading={imageActive} />
    </section>
  );
};